import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Loader2, ArrowLeft, X, Save, Phone, Mail, MapPin, Settings, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";

const STATUSES = ["NEW", "CONTACTED", "SITE VISIT", "QUOTE SENT", "FOLLOW-UP", "WON", "LOST"];
const PRIORITIES = ["high", "medium", "low"];

const statusColor = (s) => ({
  "NEW": "border-sky-400/40 text-sky-300",
  "CONTACTED": "border-indigo-400/40 text-indigo-300",
  "SITE VISIT": "border-violet-400/40 text-violet-300",
  "QUOTE SENT": "border-amber-400/40 text-amber-300",
  "FOLLOW-UP": "border-orange-400/40 text-orange-300",
  "WON": "border-emerald-400/40 text-emerald-300",
  "LOST": "border-red-400/40 text-red-300",
}[s] || "border-white/20 text-stone-300");

const priorityColor = (p) => ({
  high: "border-red-400/40 text-red-300",
  medium: "border-amber-400/40 text-amber-300",
  low: "border-stone-400/40 text-stone-400",
}[p] || "border-stone-400/40 text-stone-400");

const input = "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-3 py-2.5 text-sm outline-none transition-colors";
const label = "block text-[0.65rem] uppercase tracking-[0.18em] text-stone-500 mb-2";

const Row = ({ label: l, value }) => value ? (
  <div className="flex gap-3 text-sm py-1.5 border-b border-white/5">
    <span className="text-stone-500 w-36 shrink-0">{l}</span>
    <span className="text-stone-200 whitespace-pre-wrap">{value}</span>
  </div>
) : null;

export default function AdminLeads() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("ptwa_token") : "";

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showSettings) setShowSettings(false);
      else if (active) setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSettings, active]);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/admin/leads"), api.get("/admin/shikin-settings")]);
      setItems(l.data);
      setNotifyEmail(s.data.notify_email || "");
    } catch { toast.error("Could not load leads."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const saveLead = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${active.id}`, {
        status: active.status,
        priority: active.priority,
        notes: active.notes || "",
        follow_up_date: active.follow_up_date || "",
      });
      setItems((list) => list.map((x) => x.id === data.id ? data : x));
      setActive(data);
      toast.success("Lead updated.");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/admin/leads/${id}`); setItems((l) => l.filter((x) => x.id !== id)); setActive(null); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/shikin-settings", { notify_email: notifyEmail.trim() });
      toast.success("Notification email saved.");
      setShowSettings(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Check the email address."); }
  };

  const counts = STATUSES.reduce((acc, s) => { acc[s] = items.filter((i) => i.status === s).length; return acc; }, {});
  const shown = filter === "ALL" ? items : items.filter((i) => i.status === filter);

  if (!ready || !user) return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-leads">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin" className="inline-flex items-center gap-2 text-stone-400 hover:text-white text-xs uppercase tracking-[0.2em]"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</a>
            <span className="hidden sm:block overline text-stone-500">Shikin Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="shikin-settings-btn" onClick={() => setShowSettings(true)} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><Settings className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Settings</span></button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <h1 className="font-serif text-4xl text-stone-50 font-light mb-2">Shikin Leads</h1>
        <p className="text-stone-500 text-sm mb-8 max-w-2xl">Enquiries captured by Shikin, your 24/7 receptionist. Each lead includes the AI summary, full transcript and any photos the customer shared.</p>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid="lead-filter-ALL" onClick={() => setFilter("ALL")} className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${filter === "ALL" ? "border-[#9c7a4d] text-[#d4c4b7]" : "border-white/10 text-stone-400 hover:text-white"}`}>All ({items.length})</button>
          {STATUSES.map((s) => (
            <button key={s} data-testid={`lead-filter-${s}`} onClick={() => setFilter(s)} className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${filter === s ? "border-[#9c7a4d] text-[#d4c4b7]" : "border-white/10 text-stone-400 hover:text-white"}`}>{s} ({counts[s] || 0})</button>
          ))}
        </div>

        {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /> : (
          <div className="space-y-3">
            {shown.map((l) => (
              <button key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setActive(l)} className="w-full text-left border border-white/10 bg-[#0f0f11] hover:border-[#9c7a4d]/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 border ${statusColor(l.status)}`}>{l.status}</span>
                    <span className={`text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 border ${priorityColor(l.priority)}`}>{l.priority}</span>
                    {l.suburb ? <span className="text-[0.6rem] uppercase tracking-[0.15em] text-stone-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.suburb}</span> : null}
                  </div>
                  <p className="text-stone-100 truncate"><span className="font-medium">{l.name || "Unknown"}</span> — {l.project_type || "Tiling enquiry"}</p>
                  <p className="text-stone-500 text-xs mt-0.5 line-clamp-1">{l.summary}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-stone-400 text-xs">
                  {l.phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span> : null}
                  {(l.photos || []).length ? <span className="inline-flex items-center gap-1 text-[#d4c4b7]"><ImageIcon className="h-3 w-3" /> {l.photos.length}</span> : null}
                </div>
              </button>
            ))}
            {shown.length === 0 ? <p className="text-stone-500">No leads {filter !== "ALL" ? `with status ${filter}` : "yet"}.</p> : null}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {active ? (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-lenis-prevent onClick={() => setActive(null)}>
          <div className="bg-[#0f0f11] border border-white/10 w-full max-w-3xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-detail">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="font-serif text-2xl text-stone-50">{active.name || "Unknown customer"}</h2>
                <p className="text-stone-500 text-sm">{active.project_type || "Tiling enquiry"} · <span className="text-stone-600">{active.source}</span></p>
              </div>
              <button onClick={() => setActive(null)} className="text-stone-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            {/* Contact quick actions */}
            <div className="flex flex-wrap gap-3 mb-5">
              {active.phone ? <a href={`tel:${active.phone}`} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-200 px-4 py-2 text-xs uppercase tracking-[0.16em]"><Phone className="h-3.5 w-3.5" /> {active.phone}</a> : null}
              {active.email ? <a href={`mailto:${active.email}`} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-200 px-4 py-2 text-xs uppercase tracking-[0.16em]"><Mail className="h-3.5 w-3.5" /> {active.email}</a> : null}
            </div>

            {/* Shikin summary */}
            {active.summary ? (
              <div className="mb-5 p-4 bg-[#141416] border-l-2 border-[#9c7a4d]">
                <p className="overline text-stone-500 mb-1">Shikin's summary</p>
                <p className="text-stone-200 text-sm whitespace-pre-wrap">{active.summary}</p>
              </div>
            ) : null}

            {/* Structured details */}
            <div className="mb-5">
              <Row label="Suburb" value={active.suburb} />
              <Row label="Project details" value={active.project_description} />
              <Row label="Existing tiles" value={active.existing_tiles} />
              <Row label="Tile type" value={active.tile_type} />
              <Row label="Tile size" value={active.tile_size} />
              <Row label="Dimensions" value={active.dimensions} />
              <Row label="Special requirements" value={active.special_requirements} />
              <Row label="Timeframe" value={active.timeframe} />
              <Row label="Received" value={active.created_at ? new Date(active.created_at).toLocaleString() : ""} />
            </div>

            {/* Photos */}
            {(active.photos || []).length ? (
              <div className="mb-5">
                <p className="overline text-stone-500 mb-2">Photos ({active.photos.length})</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {active.photos.map((p) => (
                    <a key={p.id} href={`${API}/chat/photo/${p.storage_path}?auth=${token}`} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden border border-white/10">
                      <img src={`${API}/chat/photo/${p.storage_path}?auth=${token}`} alt={p.filename} className="h-full w-full object-cover hover:scale-105 transition-transform" />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Editable management */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-white/10 pt-5">
              <div>
                <label className={label}>Status</label>
                <select data-testid="lead-status-select" className={input} value={active.status} onChange={(e) => setActive({ ...active, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Priority</label>
                <select data-testid="lead-priority-select" className={input} value={active.priority} onChange={(e) => setActive({ ...active, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Follow-up date</label>
                <input type="date" className={input} value={active.follow_up_date || ""} onChange={(e) => setActive({ ...active, follow_up_date: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Internal notes</label>
                <textarea data-testid="lead-notes" rows={3} className={`${input} resize-y`} value={active.notes || ""} onChange={(e) => setActive({ ...active, notes: e.target.value })} placeholder="Add notes for the team…" />
              </div>
            </div>

            {/* Transcript */}
            <details className="mt-5">
              <summary className="cursor-pointer text-stone-400 hover:text-white text-xs uppercase tracking-[0.16em]">Conversation transcript ({(active.transcript || []).length} messages)</summary>
              <div className="mt-3 space-y-2 max-h-72 overflow-y-auto" data-lenis-prevent>
                {(active.transcript || []).map((m, i) => (
                  <div key={i} className={`text-sm ${m.role === "user" ? "text-stone-200" : "text-[#c9b79c]"}`}>
                    <span className="text-stone-600 text-xs uppercase tracking-[0.14em] mr-2">{m.role === "user" ? "Customer" : "Shikin"}</span>
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  </div>
                ))}
              </div>
            </details>

            <div className="flex flex-wrap gap-3 mt-6">
              <button data-testid="lead-save" onClick={saveLead} disabled={saving} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes</button>
              <button onClick={() => remove(active.id)} className="inline-flex items-center gap-2 text-red-300/70 hover:text-red-300 px-6 py-3 text-xs uppercase tracking-[0.2em]"><Trash2 className="h-4 w-4" /> Delete</button>
              <button onClick={() => setActive(null)} className="text-stone-400 hover:text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Settings modal */}
      {showSettings ? (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-lenis-prevent onClick={() => setShowSettings(false)}>
          <div className="bg-[#0f0f11] border border-white/10 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="shikin-settings">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-stone-50">Lead notifications</h2>
              <button onClick={() => setShowSettings(false)} className="text-stone-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className={label}>Email for new lead alerts</label>
            <input data-testid="shikin-notify-email" className={input} value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="you@business.com" />
            <p className="text-stone-500 text-xs mt-2">New Shikin leads are always saved here in the CRM. This is the inbox we also email when a lead is captured.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="shikin-settings-save" onClick={saveSettings} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] px-6 py-3 text-xs uppercase tracking-[0.2em]"><Save className="h-4 w-4" /> Save</button>
              <button onClick={() => setShowSettings(false)} className="text-stone-400 hover:text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
